import { getRouteApi, type AnyRoute, type ParseRoute } from '@tanstack/react-router';
import { type OnChangeFn, type PaginationState } from '@tanstack/react-table';
import { useCallback } from 'react';
import { z } from 'zod';

import { MAX_PAGE_SIZE } from '@airreading/common/data';

import { RegisteredRouteTree } from '@/lib/routing';

export const DEFAULT_PAGE_SIZE = 20;

export function pageSizeSchema(
  defaultPageSize: number = DEFAULT_PAGE_SIZE,
  maxPageSize: number = MAX_PAGE_SIZE,
): z.ZodCatch<z.ZodNumber> {
  return z.number().int().min(1).max(maxPageSize).catch(defaultPageSize);
}

export const paginationRequestSchema = z.object({
  pageIndex: z.number().int().catch(0),
  pageSize: pageSizeSchema(),
});

export type PaginationRequest = z.infer<typeof paginationRequestSchema>;

export type PaginatedResponse<R> = { total: number; pageIndex: number; pageSize: number } & R;

/**
 * Look up all routes whose full query string schema extends `PaginatedRequest`.
 */
export type PaginatedRoutesById<TRouteTree extends AnyRoute> = {
  [K in ParseRoute<TRouteTree> as K['types']['fullSearchSchema'] extends PaginationRequest ? K['id'] : never]: K;
};

export type PaginatedRouteId<TRouteTree extends AnyRoute> = keyof PaginatedRoutesById<TRouteTree>;

export type PaginatedRouteById<TRouteTree extends AnyRoute, TId extends PaginatedRouteId<TRouteTree>> = Extract<
  PaginatedRoutesById<TRouteTree>[TId],
  AnyRoute
>;

/**
 * Use the current pagination state and a setter for changing it. This hook is
 * only available to routes whose query string schema extends `PaginationRequest`.
 */
export function usePagination<TRouteTree extends AnyRoute = RegisteredRouteTree>(
  routeId: PaginatedRouteId<TRouteTree>,
): PaginationState & { onPaginationChange: OnChangeFn<PaginationState>; resetPagination: () => void } {
  const route = getRouteApi(routeId);
  const { pageIndex, pageSize } = route.useSearch();
  const navigate = route.useNavigate();

  const navigateOnPagingChange = useCallback(
    (newPaging: PaginationState) => {
      navigate({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: (prev: any) => ({ ...prev, pageIndex: newPaging.pageIndex, pageSize: newPaging.pageSize }) as any,
      });
    },
    [navigate],
  );

  const onPaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (updaterOrNewState) => {
      if (typeof updaterOrNewState === 'function') {
        navigateOnPagingChange(updaterOrNewState({ pageIndex, pageSize }));
      } else {
        navigateOnPagingChange(updaterOrNewState);
      }
    },
    [navigateOnPagingChange, pageIndex, pageSize],
  );

  const resetPagination = useCallback(() => {
    onPaginationChange({ pageIndex: 0, pageSize });
  }, [onPaginationChange, pageSize]);

  return { pageIndex, pageSize, onPaginationChange, resetPagination };
}
